// src/components/projects/ProjectGrid.tsx
//
// Renders every project from siteConfig.projects. Data-driven end to end:
// adding a project to the config adds a card, and nothing here names a project.
//
// The featured project is hoisted to the front and given the full-width slot.
// This mirrors how the Home page's Selected work section already treats its own
// `featured` flag, so the two sections behave the same way.
import { useReducedMotion } from 'framer-motion';

import { siteConfig } from '../../config/siteConfig';
import { motionSet } from '../about/motion';
import ProjectCard from './ProjectCard';
import { GridItem, GridRoot } from './ProjectGrid.styles';

const viewport = { once: true, amount: 0.3 } as const;

const ProjectGrid = () => {
  const reduceMotion = useReducedMotion() ?? false;
  const anim = motionSet(reduceMotion);

  const { projects } = siteConfig;

  // Same rule as Home: the first project flagged `featured` leads, and if none
  // is flagged the first entry leads instead. Everything else follows in config
  // order, so no project is dropped and no order is hardcoded here.
  const featured = projects.find((project) => project.featured);
  const ordered = featured
    ? [featured, ...projects.filter((project) => project.id !== featured.id)]
    : projects;

  return (
    <GridRoot>
      {ordered.map((project) => {
        const isFeatured = project.featured === true;

        return (
          <GridItem
            key={project.id}
            $featured={isFeatured}
            variants={anim.reveal}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            <ProjectCard project={project} featured={isFeatured} />
          </GridItem>
        );
      })}
    </GridRoot>
  );
};

export default ProjectGrid;
